/**
 * Runtime configuration.
 *
 * The image must be identical in every environment, so nothing environment-specific is
 * baked in at build time. `public/config.json` is rewritten by the container entrypoint
 * from environment variables and fetched once, before the app mounts. Build-time
 * `VITE_*` variables remain as defaults for `npm run dev`.
 *
 * Precedence: config.json > VITE_* > built-in default.
 */

/**
 * `npm run dev` answers from seeded data unless `VITE_FAKE_API=false`. Decided at build time:
 * `vite build` sets `DEV` to false whatever the mode, so no image carries the fake.
 */
export const usesFakeApi =
  import.meta.env.DEV &&
  import.meta.env.MODE === 'development' &&
  import.meta.env.VITE_FAKE_API !== 'false'

const DEFAULTS = {
  apiBaseUrl: '/api/v1',
  authDisabled: false,
  /** Entra ID, through MSAL. `tenantId` defaults to FMM's, the only one this tool serves. */
  entra: {
    tenantId: '05a4acde-bf25-48d1-a689-15bf27223c0e',
    /** Overrides the authority URL entirely, for a sovereign cloud or B2C. */
    authority: '',
    clientId: '',
    apiScope: '',
    redirectUri: '',
  },
  polling: {
    /** Preview list, while at least one preview is still moving. */
    listMs: 10_000,
    /**
     * Preview list once every preview has settled. Nothing changes without someone
     * acting, so this only has to notice another tab's creation or an expiry — neither
     * worth six requests a minute from every open tab, forever.
     */
    listIdleMs: 60_000,
    /** Preview detail while the preview is not settled. */
    detailFastMs: 3_000,
    /** Preview detail once it is Ready, Failed or Expired. */
    detailSlowMs: 15_000,
  },
}

function bool(value, fallback) {
  if (value === undefined || value === null || value === '') return fallback
  if (typeof value === 'boolean') return value
  return ['1', 'true', 'yes', 'on'].includes(String(value).toLowerCase())
}

/**
 * Merge the three sources into the effective configuration.
 *
 * Mirroring the backend's `AUTH_DISABLED`: authentication cannot be switched off in a
 * production build. The deployed image is built with `vite build`, so the flag is inert
 * there whatever `config.json` says — asserted by tests/config.spec.js.
 *
 * @param {Record<string, any>} env `import.meta.env`
 * @param {Record<string, any>} runtime parsed `config.json`, may be empty
 * @param {string} mode `import.meta.env.MODE`
 * @returns {typeof DEFAULTS & {mode: string}}
 */
export function resolveConfig(env = {}, runtime = {}, mode = 'production') {
  const isProduction = mode === 'production'
  const runtimeEntra = runtime.entra || {}

  // Development defaults to the stubbed identity, so the SPA runs without Entra.
  const isDevelopment = mode === 'development'
  const authDisabled = bool(
    runtime.authDisabled ?? env.VITE_AUTH_DISABLED,
    isDevelopment ? true : DEFAULTS.authDisabled,
  )

  return {
    mode,
    apiBaseUrl: runtime.apiBaseUrl || env.VITE_API_BASE_URL || DEFAULTS.apiBaseUrl,
    // Forced off in a production build: an image that can skip authentication is not a
    // deployable image.
    authDisabled: isProduction ? false : authDisabled,
    entra: {
      tenantId: runtimeEntra.tenantId || env.VITE_ENTRA_TENANT_ID || DEFAULTS.entra.tenantId,
      authority: runtimeEntra.authority || env.VITE_ENTRA_AUTHORITY || DEFAULTS.entra.authority,
      clientId: runtimeEntra.clientId || env.VITE_ENTRA_CLIENT_ID || DEFAULTS.entra.clientId,
      apiScope: runtimeEntra.apiScope || env.VITE_ENTRA_API_SCOPE || DEFAULTS.entra.apiScope,
      redirectUri: runtimeEntra.redirectUri || env.VITE_ENTRA_REDIRECT_URI || '',
    },
    // No domain suffix here on purpose: where a preview lands comes from the exposures
    // in `GET /applications`, so the SPA never has a second opinion about it.
    polling: { ...DEFAULTS.polling, ...(runtime.polling || {}) },
  }
}

/** The effective configuration, populated by `loadConfig()` before the app mounts. */
export const config = resolveConfig(import.meta.env, {}, import.meta.env.MODE)

/**
 * Fetch `/config.json` and fold it into `config` in place, so modules that imported the
 * object early see the final values. A missing file is not fatal: the build-time
 * defaults then apply, which is what `npm run dev` wants.
 *
 * @returns {Promise<typeof config>}
 */
export async function loadConfig() {
  let runtime = {}
  try {
    const response = await fetch('/config.json', { cache: 'no-store' })
    if (response.ok) runtime = await response.json()
  } catch {
    // No config.json (dev server, or a static host without one): keep the defaults.
  }
  Object.assign(config, resolveConfig(import.meta.env, runtime, import.meta.env.MODE))
  return config
}
